<template>
  <div>
    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
    <label
      v-if="label != null && label.length > 0"
      :for="componentId"
      class="input-select__label"
    >
      <strong>{{ label }}</strong>
      <!-- Screen readers skip the asterisk, so the word carries it. -->
      <template v-if="required">
        <span class="text-rhino" aria-hidden="true">&nbsp;*</span>
        <span class="sr-only">{{ $t('frontend/required') }}</span>
      </template>
    </label>
    <div
      ref="selectContainer"
      class="input-select relative flex"
      :class="{
        'text-mouse [&_.input-select__value:hover]:cursor-default': disabled,
      }"
    >
      <div
        class="input-select__select flex w-full border-b border-rhino"
        :class="{
          'input-select__select--open border-b-0 rounded-t shadow bg-depth-2':
            isResultListOpen,
          'cursor-pointer': !isResultListOpen,
        }"
      >
        <div
          v-if="!isResultListOpen"
          class="input-select__value flex flex-1 min-w-0 leading-none pl-4 items-center cursor-pointer"
          @click="onOpenResultList"
        >
          {{ internalPlaceholder }}

          <div class="input-select__hint ml-2">
            <fa-icon
              v-if="!disabled && !isValid"
              class="input-select__hint-icon text-danger-fg size-4"
              :icon="faExclamationCircle"
            />
          </div>
        </div>
        <input
          :id="componentId"
          ref="filterInput"
          v-model="searchQuery"
          :aria-required="required ? 'true' : undefined"
          class="flex-1 input-select__input bg-transparent px-4 py-1 border-none outline-none text-mouse -z-1"
          :class="{
            absolute: !isResultListOpen,
            'z-auto': isResultListOpen,
          }"
          type="text"
          :placeholder="internalPlaceholder"
          autocomplete="off"
          :style="{ opacity: isResultListOpen ? '1' : '0' }"
          @input="onSearchInput"
          @focus="onOpenResultList"
          @blur="onInputBlur"
        />

        <div
          v-if="!disabled"
          class="flex items-center shrink-0 cursor-pointer p-2"
          tabindex="-1"
          @click.prevent="toggleSelectOpen"
        >
          <fa-icon
            class="input-select__select-toggle size-4 text-rhino transition-transform duration-200"
            :class="{ 'rotate-180': isResultListOpen }"
            :icon="isResultListOpen ? faAngleUp : faAngleDown"
          />
        </div>
      </div>
      <div
        v-if="isResultListOpen"
        class="input-select__results-inner absolute w-full overflow-hidden rounded-b top-full bg-depth-2 z-1 shadow-md"
      >
        <div
          v-if="isResultsEmpty"
          class="input-select__results-item py-2 px-4 input-select__results-empty"
        >
          {{
            noEntryTitle.length > 0
              ? noEntryTitle
              : t('general/messages/nothing_found')
          }}
        </div>
        <ul
          v-else
          class="input-select__results relative left-0 max-h-40 overflow-y-auto"
          tabindex="-1"
        >
          <!-- grouped mode -->
          <template v-if="grouped">
            <template v-for="(filterItem, index) in filteredItems">
              <li
                v-if="getGroupItems(filterItem).length > 0"
                :key="`group__${filterItem[groupKey]}`"
              >
                <ul class="filter__results-group">
                  <!-- group header -->
                  <li
                    class="input-select__results-item block py-2 px-4 text-rhino uppercase"
                  >
                    <strong>{{ getGroupLabel(filterItem) }}</strong>
                  </li>
                  <!-- value -->
                  <form-select-item
                    v-for="groupItem in getGroupItems(filterItem)"
                    :key="groupItem[valueKey].toString()"
                    :item="groupItem"
                    :item-label-item="itemLabelFn"
                    :is-selected="isItemSelected(groupItem)"
                    :is-focused="index === itemFocusIndex"
                    :label-key="labelKey"
                    @item-selected="(item) => onItemSelected(item)"
                  />
                </ul>
              </li>
            </template>
          </template>
          <!-- ungrouped mode -->
          <template v-else>
            <form-select-item
              v-for="(filterItem, index) in filteredItems"
              :key="filterItem[valueKey].toString()"
              :item="filterItem"
              :label-key="labelKey"
              :item-label-item="itemLabelFn"
              :is-selected="isItemSelected(filterItem)"
              :is-focused="index === itemFocusIndex"
              @item-selected="(item) => onItemSelected(item)"
            />
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  faAngleDown,
  faAngleUp,
  faExclamationCircle,
} from '@fortawesome/pro-solid-svg-icons';
import {
  onClickOutside,
  onKeyStroke,
  useElementBounding,
  useMagicKeys,
} from '@vueuse/core';
import type {
  FilterSelectItemsFn,
  SelectItemLabelFn,
  SelectValueType,
} from '~/components/form/select/types';
import useItemLabel from './composables/useItemLabel';

export default defineComponent({
  props: {
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [String, Number, Object] as PropType<SelectValueType>,
      required: false,
      default: null,
    },
    query: {
      type: String,
      required: false,
      default: '',
    },
    /** @deprecated No longer used — icons use Tailwind size classes */
    resultListIconSize: {
      type: String,
      default: 'lg',
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    data: {
      type: Array as PropType<Array<Record<string, unknown>>>,
      required: true,
      default: () => [],
    },
    valueKey: {
      type: String,
      required: false,
      default: 'key',
    },
    labelKey: {
      type: String,
      required: false,
      default: 'label',
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemLabelFn: {
      type: Function as PropType<SelectItemLabelFn>,
      required: false,
      default: null,
    },
    /**
     * TODO: revisit grouped mode!
     */
    grouped: {
      type: Boolean,
      required: false,
      default: false,
    },
    groupKey: {
      type: String,
      required: false,
      default: 'key',
    },
    groupLabelKey: {
      type: String,
      required: false,
      default: 'label',
    },
    groupitemLabelFn: {
      type: Function as PropType<SelectItemLabelFn>,
      required: false,
      default: null,
    },
    groupItemsKey: {
      type: String,
      required: false,
      default: 'items',
    },
    filterItemsFn: {
      type: Function as PropType<FilterSelectItemsFn>,
      required: false,
      default: null,
    },
    isValid: {
      type: Boolean,
      default: true,
    },
    icon: {
      type: Array,
      required: false,
      default: undefined,
    },
    noEntryTitle: {
      type: String,
      required: false,
      default: '',
    },
    inputId: {
      type: String,
      required: true,
    },
  },
  emits: ['update:modelValue', 'queryChanged'],
  setup: (props, { emit }) => {
    const searchQuery = ref(props.query);
    const isResultListOpen = ref(false);
    const internalValue = ref(props.modelValue);

    // DOM stuff
    const filterInput = ref<HTMLInputElement>();
    const selectContainer = ref<HTMLDivElement>();
    const componentId = useId();

    // usability stuff
    const itemFocusIndex = ref<number>(-1);

    // validate data array
    const validatePropsData = () => {
      if (
        !props.grouped &&
        props.data.length > 0 &&
        props.data.every((item) => item[props.valueKey] === undefined)
      ) {
        throw new Error(
          `At least one element of your data must have the ${props.valueKey} property.`,
        );
      }
    };

    validatePropsData();

    const filteredItems = ref(props.data);

    const filterItemsByQuery = async (): Promise<void> => {
      if (props.filterItemsFn != null) {
        filteredItems.value = await props.filterItemsFn(
          props.data,
          searchQuery.value,
        );
      } else {
        filteredItems.value = props.data;
      }

      itemFocusIndex.value = -1;
    };

    const getItemLabel = useItemLabel(props.labelKey, props.itemLabelFn);

    const getInternalValueLabel = () => {
      if (typeof internalValue.value === 'object') {
        return getItemLabel(internalValue.value);
      }

      if (
        typeof internalValue.value === 'string' ||
        typeof internalValue.value === 'number'
      ) {
        const labelledItem = props.data.find(
          (item) => item[props.valueKey] === internalValue.value.toString(),
        );
        return getItemLabel(labelledItem);
      }

      return '';
    };

    const setValue = (newValue: SelectValueType) => {
      internalValue.value = newValue;
      if (newValue == null) {
        searchQuery.value = '';
      } else {
        searchQuery.value = getInternalValueLabel();
      }
    };

    const onItemSelected = (filterItem) => {
      if (
        internalValue.value != null &&
        filterItem[props.valueKey] === internalValue.value[props.valueKey] &&
        props.nullable
      ) {
        internalValue.value = null;
      }

      setValue(filterItem);

      // respect original typing of value prop
      if (typeof props.modelValue === 'object') {
        emit('update:modelValue', filterItem);
      } else {
        emit('update:modelValue', filterItem[props.valueKey]);
      }
      isResultListOpen.value = false;
    };

    // when outside value changes -> set internal value and update search query
    watch(
      () => props.modelValue,
      (newValue) => {
        // force setting when value is null -> preselect first element instead
        if (!props.nullable && newValue == null) {
          setValue(props.data[0]);
        }

        setValue(newValue);
      },
    );

    watch(
      () => props.data,
      () => {
        validatePropsData();

        // do not change value in search mode and our select is nullable
        // and its value is null
        if (props.nullable && internalValue.value == null) return;

        // force setting value to null when data source is non existent
        if (props.data.length === 0) {
          setValue(null);
          emit('update:modelValue', null);
        }

        filterItemsByQuery();
      },
    );

    watch(
      () => props.query,
      () => {
        searchQuery.value = props.query;

        filterItemsByQuery();
      },
    );

    watch(() => props.valueKey, validatePropsData);

    const getGroupLabel = (group: Record<string, unknown>) => {
      if (group == null) return '';
      if (props.groupitemLabelFn != null)
        return props.groupitemLabelFn.call(null, group);
      return group[props.groupLabelKey];
    };

    const onOpenResultList = async () => {
      if (props.disabled) return;

      if (!isResultListOpen.value) {
        searchQuery.value = '';
        isResultListOpen.value = true;
        filterItemsByQuery();

        await nextTick();
        filterInput.value.focus();
        itemFocusIndex.value = -1;
      }
    };

    const onCloseResultList = () => {
      const internalValueLabel = getInternalValueLabel();
      if (searchQuery.value != internalValueLabel) {
        searchQuery.value = internalValueLabel;
      }

      isResultListOpen.value = false;
      itemFocusIndex.value = -1;
      filterInput.value.blur();
    };

    const onClearQuery = () => {
      if (props.nullable) {
        internalValue.value = null;
      }

      searchQuery.value = '';
      emit('queryChanged', '');
      filterItemsByQuery();
    };

    const onSearchInput = async () => {
      isResultListOpen.value = true;
      emit('queryChanged', searchQuery.value);
      filterItemsByQuery();
    };

    const isItemSelected = (filterItem: Record<string, unknown>) => {
      if (internalValue.value == null) return false;
      return filterItem[props.valueKey] === internalValue.value[props.valueKey];
    };

    const getGroupItems = (group) =>
      group[props.groupItemsKey] as Array<Record<string, unknown>>;

    const internalPlaceholder = computed(() =>
      internalValue.value == null ? props.placeholder : getInternalValueLabel(),
    );

    const isResultsEmpty = computed(
      () =>
        filteredItems.value.length == 0 ||
        (props.grouped &&
          filteredItems.value.every(
            (group) => getGroupItems(group).length === 0,
          )),
    );

    // handle focus item with keyboard
    const scrollInResultContainer = () => {
      const focusedItemEl = selectContainer.value.querySelectorAll(
        '.input-select__results-item',
      )[itemFocusIndex.value] as HTMLElement;
      const { height: selectItemHeight } = useElementBounding(focusedItemEl);
      const selectContainerEl = selectContainer.value.querySelector(
        '.input-select__results',
      );

      selectContainer.value.querySelector('.input-select__results').scrollTo({
        top:
          focusedItemEl.offsetTop -
          selectContainerEl.clientHeight +
          selectItemHeight.value,
      });
    };

    onKeyStroke('ArrowDown', (event) => {
      if (isResultListOpen.value) {
        event.preventDefault();

        if (itemFocusIndex.value < filteredItems.value.length - 1) {
          itemFocusIndex.value += 1;
        } else if (itemFocusIndex.value === filteredItems.value.length - 1) {
          itemFocusIndex.value = filteredItems.value.length - 1;
        }

        scrollInResultContainer();
      }
    });

    onKeyStroke('ArrowUp', (event) => {
      if (isResultListOpen.value) {
        event.preventDefault();

        if (itemFocusIndex.value > 0) {
          itemFocusIndex.value -= 1;
        } else if (itemFocusIndex.value === -1) {
          itemFocusIndex.value = 0;
        }

        scrollInResultContainer();
      }
    });

    const { enter } = useMagicKeys({
      passive: false,
      onEventFired: (e) => {
        // prevent falsely firing submit when in form
        if (
          e.key === 'Enter' &&
          isResultListOpen.value &&
          itemFocusIndex.value >= 0
        ) {
          e.preventDefault();
        }
      },
    });

    watch(
      () => enter.value,
      (newValue) => {
        if (newValue && isResultListOpen.value) {
          // is new value
          if (
            itemFocusIndex.value >= 0 &&
            filteredItems.value[itemFocusIndex.value] != null
          ) {
            onItemSelected(filteredItems.value[itemFocusIndex.value]);
          }

          // is current value - we have to force closing the result container
          if (
            itemFocusIndex.value >= -1 &&
            filteredItems.value.length === 1 &&
            filteredItems.value[0][props.valueKey] ===
              internalValue.value[props.valueKey]
          ) {
            onItemSelected(filteredItems.value[0]);
          }
        }
      },
    );

    const onInputBlur = (event: FocusEvent) => {
      if (
        event.relatedTarget != null &&
        ((event.relatedTarget as HTMLElement).classList.contains(
          'input-select__results',
        ) ||
          (event.relatedTarget as HTMLElement).classList.contains(
            'input-select__select-toggle',
          ))
      ) {
        return;
      }

      if (isResultListOpen.value) {
        onCloseResultList();
      }
    };

    onClickOutside(selectContainer, () => {
      onCloseResultList();
    });

    const toggleSelectOpen = (event: PointerEvent) => {
      event.preventDefault();
      // avoid open when enter is pressed
      if (event.pointerType != null && event.pointerType.length === 0) return;

      if (isResultListOpen.value) {
        onCloseResultList();
      } else {
        onOpenResultList();
      }
    };

    const { t } = useI18n();

    return {
      faAngleDown,
      faAngleUp,
      faExclamationCircle,

      t,

      componentId,
      selectContainer,
      filterInput,
      isResultListOpen,
      itemFocusIndex,
      searchQuery,
      internalValue,
      filteredItems,
      internalPlaceholder,
      isResultsEmpty,
      getItemLabel,
      getGroupLabel,
      isItemSelected,
      getGroupItems,
      toggleSelectOpen,
      onOpenResultList,
      onCloseResultList,
      onItemSelected,
      onClearQuery,
      onSearchInput,
      onInputBlur,
    };
  },
});
</script>

<style scoped>
/* Pseudo-elements require <style> — cannot be expressed as Tailwind utilities */
.input-select__label::first-letter {
  text-transform: capitalize;
}

.input-select__results-inner::before {
  content: '';
  width: 98%;
  height: 1px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--color-mouse);
}
</style>
